/**
 * Maps a bank action from the shape the table edits into the shape
 * `PUT /banks/actions/:bankId` binds.
 *
 * The endpoint declares no `fbSerializer`, so `createFlatBufferBaseQuery` sends
 * the body as plain JSON with whatever keys the object already carries. The
 * table's rows are camelCase (`messageId`, `toggleTimer`); LiftCloud binds
 * `models.ActionBankReq`, which is snake_case (`message_id`, `toggle_timer`).
 * Nothing converted between the two, so every required field arrived absent and
 * the server answered 400 on `message_id`, `bank_id` and `timer2` — for every
 * create and every edit, on every bank.
 *
 * Optional ids are omitted rather than sent empty. Their binding tags are
 * `omitempty,uuid`, and an empty string is present-but-not-a-UUID, which fails
 * where absent would have passed.
 */

import { BankActionInterface } from "../../interfaces/Bank.interface";
import formatUuidT from "../../flatbuffers/display/uuid";

/** The JSON body of one action, as `models.ActionBankReq` binds it. */
export interface ActionBankRequestBody {
  message_id: string;
  bank_id: string;
  button_type: string;
  label: string;
  color: string;
  timer: number;
  valid_states: string[];
  toggle_color: string;
  toggle_label: string;
  toggle_timer: number;
  toggle_valid_states: string[];
  /**
   * Legacy alias for the toggle timer. `resolveToggleVisualFields` reads
   * `toggle_timer` first and falls back to this, but the binding tag on
   * `timer2` is `gt=0` with no `omitempty`, so it is validated on every
   * request whether or not the server ends up using it. Sending the toggle
   * timer under both names satisfies the validator and the fallback at once.
   */
  timer2: number;
  /** Legacy alias for the toggle colour, same fallback, no binding tag. */
  color2: string;
  action_id?: string;
  toggle_action_id?: string;
  toggle_message_id?: string;
}

/** The full request body: rows to write, plus message ids to remove. */
export interface UpdateBankActionsRequestBody {
  actions: ActionBankRequestBody[];
  actions2delete: string[];
}

/**
 * Ids reach the table as decoded FlatBuffer `UuidT`s even though the row
 * interface declares them `string`, so every id crossing into a request goes
 * through `formatUuidT` rather than template interpolation.
 */
function idOf(value: unknown): string {
  return formatUuidT(value as Parameters<typeof formatUuidT>[0]);
}

/**
 * `{ [key]: id }` when `value` is a non-empty id, and `{}` otherwise, so
 * spreading it leaves the key absent rather than present-and-empty.
 */
function optionalId<K extends string>(key: K, value: unknown): Partial<Record<K, string>> {
  const id = idOf(value);
  return id ? ({ [key]: id } as Record<K, string>) : {};
}

/**
 * Converts one edited row into its request body.
 *
 * `bankId` is passed separately because a newly added row has never been
 * persisted and so carries no `bankId` of its own — the server rejects
 * `bank_id` as required, which is one of the three errors a create used to
 * fail on.
 */
export function toActionBankRequest(
  action: BankActionInterface,
  bankId: unknown,
): ActionBankRequestBody {
  const toggleTimer = action.toggleTimer ?? 0;

  return {
    message_id: idOf(action.messageId),
    bank_id: idOf(bankId) || idOf(action.bankId),
    button_type: action.buttonType ?? "",
    label: action.label ?? "",
    color: action.color ?? "",
    timer: action.timer ?? 0,
    valid_states: (action.validStates ?? []).map(idOf).filter(Boolean),
    toggle_color: action.toggleColor ?? "",
    toggle_label: action.toggleLabel ?? "",
    toggle_timer: toggleTimer,
    toggle_valid_states: (action.toggleValidStates ?? []).map(idOf).filter(Boolean),
    timer2: toggleTimer,
    color2: action.toggleColor ?? "",
    ...optionalId("action_id", action.actionId),
    ...optionalId("toggle_action_id", action.toggleActionId),
    ...optionalId("toggle_message_id", action.toggleMessageId),
  };
}

/** Builds the whole `PUT /banks/actions/:bankId` body. */
export function toUpdateBankActionsRequest(
  actions: BankActionInterface[],
  actions2delete: unknown[],
  bankId: unknown,
): UpdateBankActionsRequestBody {
  return {
    actions: actions.map((action) => toActionBankRequest(action, bankId)),
    actions2delete: actions2delete.map(idOf).filter(Boolean),
  };
}
